import { useMemo, useState } from "react";
import {
  ExactMatchColumnsForFiltersForDTO,
  SelectColumnsForFiltersForDTO,
  type IDataFilters,
  type IExactMatchFilterItem,
  type IRangeFilterItem,
  type RangeColumnsForFiltersForDTO,
} from "@/api/molecularData";
import {
  EXACT_MATCH_FIELD_CONFIG,
  RANGE_FIELD_CONFIG,
  formatRangeValue,
} from "@/lib/filterFieldConfig";

export type ExactMatchDraft = Partial<
  Record<ExactMatchColumnsForFiltersForDTO, string>
>;
export type RangeDraft = Partial<
  Record<RangeColumnsForFiltersForDTO, { min: number; max: number }>
>;

/** Everything the "More filters" panel controls, committed to the page. */
export interface IFilterPanelState {
  sourceIds: string[];
  exactMatch: ExactMatchDraft;
  ranges: RangeDraft;
}

/** One removable pill shown in the active-filters summary bar. */
export interface IActiveFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

const EMPTY_PANEL_STATE: IFilterPanelState = {
  sourceIds: [],
  exactMatch: {},
  ranges: {},
};

/**
 * Owns all BSA-DB data table filter state: the Viruses sidebar (applied
 * immediately) plus the Source IDs / ID lookup / range fields committed via
 * the FilterPanel modal. Derives the exact `selectFilters` /
 * `exactMatchFilters` / `rangeFilters` payload to send, plus a flat list of
 * removable chips for the active-filters summary bar.
 */
export const useDataFilters = () => {
  const [selectedViruses, setSelectedViruses] = useState<string[]>(() => {
    try {
      const stored = sessionStorage.getItem("bsad_virus_filter");
      if (stored) {
        sessionStorage.removeItem("bsad_virus_filter");
        return JSON.parse(stored) as string[];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const [panelState, setPanelState] =
    useState<IFilterPanelState>(EMPTY_PANEL_STATE);

  const toggleVirus = (virusName: string) => {
    setSelectedViruses((prev) =>
      prev.includes(virusName)
        ? prev.filter((v) => v !== virusName)
        : [...prev, virusName],
    );
  };

  const clearViruses = () => setSelectedViruses([]);

  const applyFilterPanel = (next: IFilterPanelState) => setPanelState(next);
  const clearFilterPanel = () => setPanelState(EMPTY_PANEL_STATE);
  const clearAllFilters = () => {
    clearViruses();
    clearFilterPanel();
  };

  const removeSourceId = (id: string) =>
    setPanelState((prev) => ({
      ...prev,
      sourceIds: prev.sourceIds.filter((existing) => existing !== id),
    }));

  const removeExactMatchFilter = (field: ExactMatchColumnsForFiltersForDTO) =>
    setPanelState((prev) => {
      const exactMatch = { ...prev.exactMatch };
      delete exactMatch[field];
      return { ...prev, exactMatch };
    });

  const removeRangeFilter = (field: RangeColumnsForFiltersForDTO) =>
    setPanelState((prev) => {
      const ranges = { ...prev.ranges };
      delete ranges[field];
      return { ...prev, ranges };
    });

  const exactMatchFilters = useMemo<IExactMatchFilterItem[]>(
    () =>
      Object.entries(panelState.exactMatch)
        .filter(([, value]) => !!value && value.trim() !== "")
        .map(([field, value]) => ({
          field: field as ExactMatchColumnsForFiltersForDTO,
          value: (value as string).trim(),
        })),
    [panelState.exactMatch],
  );

  const rangeFilters = useMemo<IRangeFilterItem[]>(
    () =>
      Object.entries(panelState.ranges).map(([field, range]) => ({
        field: field as RangeColumnsForFiltersForDTO,
        min: range!.min,
        max: range!.max,
      })),
    [panelState.ranges],
  );

  const filters = useMemo<IDataFilters>(() => {
    const selectFilters = [
      selectedViruses.length > 0
        ? {
            field: SelectColumnsForFiltersForDTO.VIRUS_NAME,
            value: selectedViruses,
          }
        : null,
      panelState.sourceIds.length > 0
        ? {
            field: SelectColumnsForFiltersForDTO.SOURCE_IDS,
            value: panelState.sourceIds,
          }
        : null,
    ].filter((f): f is NonNullable<typeof f> => f !== null);

    return { selectFilters, exactMatchFilters, rangeFilters };
  }, [selectedViruses, panelState.sourceIds, exactMatchFilters, rangeFilters]);

  const activeFilterCount =
    (selectedViruses.length > 0 ? 1 : 0) +
    (panelState.sourceIds.length > 0 ? 1 : 0) +
    exactMatchFilters.length +
    rangeFilters.length;

  const activeFilterChips: IActiveFilterChip[] = [
    ...selectedViruses.map((virus) => ({
      key: `virus-${virus}`,
      label: `Virus: ${virus}`,
      onRemove: () => toggleVirus(virus),
    })),
    ...panelState.sourceIds.map((id) => ({
      key: `source-${id}`,
      label: `Source ID: ${id}`,
      onRemove: () => removeSourceId(id),
    })),
    ...exactMatchFilters.map((f) => ({
      key: `exact-${f.field}`,
      label: `${EXACT_MATCH_FIELD_CONFIG[f.field].label}: ${f.value}`,
      onRemove: () => removeExactMatchFilter(f.field),
    })),
    ...rangeFilters.map((f) => ({
      key: `range-${f.field}`,
      label: `${RANGE_FIELD_CONFIG[f.field].label}: ${formatRangeValue(f.min!, f.field)} – ${formatRangeValue(f.max!, f.field)}`,
      onRemove: () => removeRangeFilter(f.field),
    })),
  ];

  return {
    selectedViruses,
    toggleVirus,
    clearViruses,
    panelState,
    applyFilterPanel,
    clearFilterPanel,
    clearAllFilters,
    filters,
    activeFilterCount,
    activeFilterChips,
  };
};
